import { AxiosResponse } from 'axios';
import { Reminder } from '../interfaces/reminder';
import { ReminderPreview } from '../interfaces/reminder-preview';
import axiosInstance, { axiosInstanceErrorOn202 } from './axios-instance';
import axiosRetry from 'axios-retry';
import { ReminderWorkflow } from '../interfaces/reminder-workflow';
import alertToasts from '../components/ui/alerts/alert-toast';

const getReminderWorkflows = ({
  masterRef,
}: {
  masterRef: string;
  id?: string;
}): Promise<ReminderWorkflow[]> =>
  axiosInstance.get(`/api/masters/${masterRef}/reminder-workflows`, {
    withCredentials: true,
  });

const createReminder = ({
  masterRef,
  reminder,
}: {
  masterRef: string;
  reminder: Reminder;
}): Promise<AxiosResponse> =>
  axiosInstance.post(`/api/masters/${masterRef}/reminders`, reminder);

const previewReminder = ({
  masterRef,
  reminderPreview,
}: {
  masterRef: string;
  reminderPreview: ReminderPreview;
}): Promise<ArrayBuffer> =>
  axiosInstance.post(
    `/api/masters/${masterRef}/reminders/preview`,
    reminderPreview,
    { responseType: 'arraybuffer' },
  );

const printLettersByTiers = ({
  masterRef,
  tiersRefs,
}: {
  masterRef: string;
  tiersRefs: string[];
}): Promise<ArrayBuffer> =>
  axiosInstance.post(
    `/api/masters/${masterRef}/reminders/print-letters`,
    { tiersRefs },
    { responseType: 'arraybuffer' },
  );

const printLettersByTiersV2 = ({
  masterRef,
  tiersRefs,
}: {
  masterRef: string;
  tiersRefs: string[];
}): Promise<ArrayBuffer> =>
  axiosInstance.post(`/api/v2/masters/${masterRef}/reminders/print-letters`, {
    tiersRefs,
  });

const printBatchDownload = ({
  batchId,
}: {
  batchId: string;
}): Promise<ArrayBuffer> => {
  axiosRetry(axiosInstanceErrorOn202, {
    retries: 10,
    retryCondition: () => true,
    retryDelay: () => 4000,
  });
  return axiosInstanceErrorOn202
    .get(`/api/v2/print-batches/${batchId}/download`, {
      responseType: 'arraybuffer',
    })
    .then((response) => {
      return response.data;
    })
    .catch(() => {});
};

const getFile = ({
  url,
  useToastError = true,
}: {
  url: string;
  useToastError?: boolean;
}): Promise<any> => {
  return axiosInstance
    .get(url, {
      responseType: 'arraybuffer',
      transformResponse: [
        function (data, headers) {
          return { data, headers };
        },
      ],
    })
    .catch(() => {
      if (useToastError) {
        alertToasts({
          type: 'error',
          translateKey: 'toast.error',
        });
      }
    });
};

const reminderService = {
  getReminderWorkflows,
  createReminder,
  previewReminder,
  printLettersByTiers,
  printLettersByTiersV2,
  printBatchDownload,
  getFile,
};

export default reminderService;
